import { useState, useEffect } from "react";
import "./css/adminfaq.css";
import AdminFaqDetail from "./modal/AdminFaqDetail";
import AdminFaqAdd from "./modal/AdminFaqAdd";
import {
  getFaqList,
  deleteFaqList,
  searchFaq,
} from "../../utils/adminFaqService";
import { formatDateTime24 } from "../../utils/commonService";
import Pagination from "../common/Pagination";

const AdminFaq = () => {
  const [selectedFaq, setSelectedFaq] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [showCheckbox, setShowCheckbox] = useState(false);
  const [selectedFaqs, setSelectedFaqs] = useState([]);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 10;

  // 검색 관련 상태
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: new Date().toISOString().split("T")[0],
    faqType: "",
    userType: "",
    searchType: "all",
    searchKeyword: "",
  });

  // FAQ 목록 조회
  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async (page = 0) => {
    try {
      const result = await getFaqList(page, pageSize);
      if (result.success && result.data) {
        setFaqs(result.data.content || []);
        setCurrentPage(result.data.number || 0);
        setTotalPages(result.data.totalPages || 0);
        setTotalElements(result.data.totalElements || 0);
      } else {
        setFaqs([]);
      }
    } catch (error) {
      setFaqs([]);
    }
  };

  const handleSearchInternal = async (page = 0) => {
    try {
      const searchParams = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        faqType: filters.faqType,
        userType: filters.userType,
        searchType: filters.searchType,
        searchTerm: filters.searchKeyword,
      };
      const result = await searchFaq(searchParams, page, pageSize);
      if (result.success && result.data) {
        setFaqs(result.data.content || []);
        setCurrentPage(result.data.number || 0);
        setTotalPages(result.data.totalPages || 0);
        setTotalElements(result.data.totalElements || 0);
      } else {
        alert("검색 중 오류가 발생했습니다.");
      }
    } catch (error) {
      console.error("검색 중 오류:", error);
      alert("검색 중 오류가 발생했습니다.");
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    isSearchMode ? handleSearchInternal(page) : fetchFaqs(page);
  };

  const handleSearch = () => {
    setCurrentPage(0);
    setIsSearchMode(true);
    handleSearchInternal(0);
  };

  const handleReset = () => {
    setFilters({
      startDate: "",
      endDate: new Date().toISOString().split("T")[0],
      faqType: "",
      userType: "",
      searchType: "all",
      searchKeyword: "",
    });
    setIsSearchMode(false);
    setCurrentPage(0);
    fetchFaqs(0);
  };

  const handleCheckboxChange = (faqIdx) => {
    setSelectedFaqs((prev) => {
      if (prev.includes(faqIdx)) {
        return prev.filter((idx) => idx !== faqIdx);
      } else {
        return [...prev, faqIdx];
      }
    });
  };

  const toggleDeleteMode = () => {
    setShowCheckbox(!showCheckbox);
    setSelectedFaqs([]);
  };

  const handleDelete = async () => {
    if (selectedFaqs.length === 0) {
      alert("삭제할 FAQ를 선택해주세요.");
      return;
    }

    if (
      !window.confirm(
        `선택한 ${selectedFaqs.length}개의 FAQ를 삭제하시겠습니까?`,
      )
    ) {
      return;
    }

    const result = await deleteFaqList(selectedFaqs);
    if (result.success) {
      alert(`선택한 ${selectedFaqs.length}개의 FAQ가 삭제되었습니다.`);
      setSelectedFaqs([]);
      setShowCheckbox(false);
      fetchFaqs(currentPage);
    } else {
      alert(result.error);
    }
  };

  return (
    <div className="adminfaq-container">
      <div className="adminfaq-wrap">
        {/* 검색 필터 */}
        <div className="adminfaq-filter-wrap">
          <div className="adminfaq-filter-row">
            <div className="adminfaq-filter-group">
              <label className="adminfaq-filter-label">조회기간</label>
              <div className="adminfaq-date-group">
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => {
                    const today = new Date().toISOString().split("T")[0];
                    setFilters({
                      ...filters,
                      startDate: e.target.value,
                      endDate: today,
                    });
                  }}
                  className="adminfaq-date-input"
                />
                <span className="adminfaq-date-separator">~</span>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) =>
                    setFilters({ ...filters, endDate: e.target.value })
                  }
                  className="adminfaq-date-input"
                />
              </div>
            </div>
          </div>

          <div className="adminfaq-filter-divider"></div>

          <div className="adminfaq-filter-row">
            <div className="adminfaq-filter-group">
              <label className="adminfaq-filter-label">대상</label>
              <select
                value={filters.userType}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    userType: e.target.value,
                    faqType: "",
                  })
                }
                className="adminfaq-select"
              >
                <option value="">전체</option>
                <option value="CUST">사용자</option>
                <option value="STORE">가맹점</option>
              </select>
            </div>

            <div className="adminfaq-filter-group">
              <label className="adminfaq-filter-label">분류</label>
              <select
                value={filters.faqType}
                onChange={(e) =>
                  setFilters({ ...filters, faqType: e.target.value })
                }
                className="adminfaq-select"
                disabled={!filters.userType}
              >
                <option value="">전체</option>
                {filters.userType === "CUST" && (
                  <>
                    <option value="CUST_ACCOUNT">회원/계정</option>
                    <option value="CUST_ORDER_PAYMENT">주문/결제</option>
                    <option value="CUST_REFUND">취소/환불</option>
                    <option value="ETC">기타</option>
                  </>
                )}
                {filters.userType === "STORE" && (
                  <>
                    <option value="STORE_ACCOUNT">회원/계정</option>
                    <option value="STORE_SETTLEMENT">정산/결제</option>
                    <option value="STORE_PRODUCT_ORDER">상품/주문</option>
                    <option value="STORE_OPERATION">시스템/운영</option>
                  </>
                )}
              </select>
            </div>
          </div>

          <div className="adminfaq-filter-row">
            <div className="adminfaq-filter-group">
              <label className="adminfaq-filter-label">검색</label>
              <div className="adminfaq-search-group">
                <select
                  value={filters.searchType}
                  onChange={(e) =>
                    setFilters({ ...filters, searchType: e.target.value })
                  }
                  className="adminfaq-select"
                >
                  <option value="all">전체</option>
                  <option value="title">제목</option>
                  <option value="content">내용</option>
                  <option value="regnm">작성자</option>
                  <option value="modnm">수정자</option>
                </select>
                <input
                  type="text"
                  placeholder="검색어를 입력해주세요."
                  value={filters.searchKeyword}
                  onChange={(e) =>
                    setFilters({ ...filters, searchKeyword: e.target.value })
                  }
                  className="adminfaq-search-input"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                />
              </div>
            </div>

            <div className="adminfaq-filter-btn-group">
              <button className="adminfaq-search-btn" onClick={handleSearch}>
                검색
              </button>
              <button className="adminfaq-reset-btn" onClick={handleReset}>
                초기화
              </button>
            </div>
          </div>
        </div>

        <div className="adminfaq-table-wrap">
          <div className="adminfaq-table-header">
            <div className="adminfaq-table-info">
              총 <span className="adminfaq-count-bold">{totalElements}</span>건
            </div>
            <div className="adminfaq-table-actions">
              {showCheckbox ? (
                ""
              ) : (
                <button
                  className="adminfaq-write-btn"
                  onClick={() => setIsAddModalOpen(true)}
                >
                  작성
                </button>
              )}
              {showCheckbox && (
                <button
                  className="adminfaq-delete-confirm-btn"
                  onClick={handleDelete}
                >
                  삭제
                </button>
              )}
              <button
                className={`adminfaq-delete-btn ${
                  showCheckbox ? "active" : ""
                }`}
                onClick={toggleDeleteMode}
              >
                {showCheckbox ? "취소" : "삭제"}
              </button>
            </div>
          </div>

          <table className="adminfaq-table">
            <colgroup>
              {showCheckbox && <col style={{ width: "5%" }} />}
              <col style={{ width: "9%" }} />
              <col style={{ width: "7%" }} />
              <col />
              <col style={{ width: "12%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "6%" }} />
            </colgroup>
            <thead>
              <tr>
                {showCheckbox && <th>선택</th>}
                <th>대상</th>
                <th>분류</th>
                <th>제목</th>
                <th>작성자</th>
                <th>수정자</th>
                <th>최종수정일</th>
                <th>상세</th>
              </tr>
            </thead>
            <tbody>
              {faqs && faqs.length > 0 ? (
                faqs.map((faq, index) => (
                  <tr key={faq.faqIdx}>
                    {showCheckbox && (
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedFaqs.includes(faq.faqIdx)}
                          onChange={() => handleCheckboxChange(faq.faqIdx)}
                        />
                      </td>
                    )}
                    <td>{faq.userType || "-"}</td>
                    <td>{faq.faqType || "-"}</td>
                    <td className="adminfaq-title">{faq.faqTitle}</td>
                    <td>{faq.regNm}</td>
                    <td>{faq.modNm || "-"}</td>
                    <td className="adminfaq-date-cell">
                      {formatDateTime24(faq.modDateTime)}
                    </td>
                    <td>
                      <button
                        className="adminfaq-btn adminfaq-btn-detail"
                        onClick={() => {
                          setSelectedFaq(faq.faqIdx);
                          setIsModalOpen(true);
                        }}
                      >
                        보기
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={showCheckbox ? "10" : "9"}
                    style={{ textAlign: "center" }}
                  >
                    등록된 FAQ가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* 페이지네이션 */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      {/* 모달 */}
      {isModalOpen && selectedFaq && (
        <AdminFaqDetail
          faqIdx={selectedFaq}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedFaq(null);
          }}
          onUpdate={() => {
            if (isSearchMode) {
              handleSearchInternal(currentPage);
            } else {
              fetchFaqs(currentPage);
            }
          }}
        />
      )}

      {/* FAQ작성 모달 */}
      {isAddModalOpen && (
        <AdminFaqAdd
          onClose={() => setIsAddModalOpen(false)}
          onAdd={() => {
            if (isSearchMode) {
              handleSearchInternal(0);
            } else {
              fetchFaqs(0);
            }
          }}
        />
      )}
    </div>
  );
};

export default AdminFaq;
